import React, { useContext } from 'react';
import {
  Button, Card, CardActions, CardContent, CardHeader, Divider, Grid, MenuItem, Select,
  OutlinedInput, FormControl, InputLabel
} from '@material-ui/core';
import { FieldArray, Formik } from 'formik';
import { PathEditorContext } from '.';
import * as Edit from '../../common/pathEditTypes';
import { LanguageString, LSTextField, TextField } from '../Common';
import { updateLs } from '../MilestoneEditor/MilestoneEditorForm';
import { SubGroupList } from './SubgroupsList';
import { SiteContext } from '../../contexts';
import * as DTO from '../../dto';


const reportingFrequencyOptions = [
  { id: 1, text: 'Daily', value: DTO.ReportingFrequency.DAILY },
  { id: 2, text: 'Weekly', value: DTO.ReportingFrequency.WEEKLY },
  { id: 3, text: 'Monthly', value: DTO.ReportingFrequency.MONTHLY }
];

export const PathDetail: React.FC<{ path: Edit.SuccessPath }> = ({ path }) => {
  const { hasUnsavedChanges, refresh } = useContext(PathEditorContext);
  const siteCtx = useContext(SiteContext);
  const handleSubmit = (values: Edit.SuccessPath) => {
    updateLs(path, values, 'name');
    updateLs(path, values, 'description');
    path.reportingFrequency = siteCtx.site?.mtsNonDailyReporting ? values.reportingFrequency : DTO.ReportingFrequency.DAILY;
    path.mtsView = values.mtsView;
    path.viewOptions = values.viewOptions;
    refresh();
  };

  const studentNavRoutes = siteCtx.site?.navRoutes?.find(route => route.role === DTO.Role.STUDENT);
  const mtsRoute = studentNavRoutes?.children?.find(childRoute => childRoute.component === DTO.NavComponent.MTS);
  const mtsChildRoutes = mtsRoute?.children?.filter(childRoute => childRoute.mtsView !== null);
  const mtsSelectOptions = mtsChildRoutes ? mtsChildRoutes?.map(childRoute => ({
    id: childRoute.id,
    text: childRoute.mtsView,
    value: childRoute.mtsView
  })) : [
    { id: 1, text: 'mts-week', value: DTO.MTSView.week },
    { id: 2, text: 'mts-month', value: DTO.MTSView.month },
    { id: 3, text: 'mts-year', value: DTO.MTSView.year }
  ];

  return (
    <Formik initialValues={path} onSubmit={handleSubmit}>
      {formik => {
        hasUnsavedChanges.current = formik.dirty;
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title={<LanguageString groupName="EDITOR" resourceName="PATH" />} />
                <Divider />
                <CardContent>
                  <Grid container spacing={2} direction="column">
                    <LSTextField id="path_name" item name="name" label="Name" />
                    <LSTextField id="path_desc" item name="description" label="Description" />
                    {siteCtx.site?.mtsNonDailyReporting &&
                      <Grid item>
                        <TextField
                          id="reportingFrequency"
                          name="reportingFrequency"
                          label="Reporting Frequency"
                          select
                          onChange={formik.handleChange}
                        >
                          {reportingFrequencyOptions.map(option => (
                            <MenuItem key={option.id} value={option.value}>
                              {option.text}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    }
                    {siteCtx.site?.pathMtsViewSelection && (
                      <>
                        <Grid item>
                          <TextField
                            id="mtsView"
                            name="mtsView"
                            label="Initial MTS View"
                            select
                          >
                            {mtsSelectOptions.map(option => (
                              <MenuItem key={option.id} value={option.value}>
                                {option.text}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel id={`view_options_label`}>View Options</InputLabel>
                            <Select
                              id="viewOptions"
                              name="viewOptions"
                              labelId={`view_options_label`}
                              label="View Options"
                              multiple
                              fullWidth
                              input={<OutlinedInput label="View Options" />}
                              onChange={formik.handleChange}
                              value={formik.values.viewOptions || []}
                            >
                              {mtsSelectOptions.map(option => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.text}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                      )}
                  </Grid>
                </CardContent>
                <CardActions><Button onClick={formik.submitForm}>Save</Button></CardActions>
              </Card>
            </Grid>
            {path.msGroups?.length && (
              <Grid item xs={12}>
                <FieldArray name="msGroups" component={SubGroupList} />
              </Grid>
            )}
          </Grid>
        );
      }}
    </Formik>
  );
};
